import React, { useState, useEffect, useCallback } from 'react';
import { 
  Briefcase, Plus, Edit, Trash2, X, Calendar, Mail, Phone, Globe, 
  ExternalLink, CheckCircle, AlertCircle, Loader2, Clock, ChevronDown,
  Eye, MousePointerClick, BarChart3
} from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// Job Card Component
const JobCard = ({ job, onEdit, onClose, onDelete, isLoading, showAnalytics = false }) => {
  const isActive = job.status === 'active';
  const isExpired = job.status === 'expired';
  const isClosed = job.status === 'closed';
  
  const getStatusBadge = () => {
    if (isActive) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <CheckCircle className="w-3 h-3" />
          Ativa
        </span>
      );
    }
    if (isExpired) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
          <Clock className="w-3 h-3" />
          Expirada
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
        <AlertCircle className="w-3 h-3" />
        Encerrada
      </span>
    );
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getApplicationLinks = () => {
    const links = job.application_links || {};
    const items = [];
    
    if (links.email) {
      items.push(
        <a 
          key="email" 
          href={`mailto:${links.email}`} 
          className="flex items-center gap-1 text-sm text-teal-600 hover:underline"
        >
          <Mail className="w-4 h-4" />
          {links.email}
        </a>
      );
    }
    if (links.whatsapp) {
      const formattedPhone = links.whatsapp.replace(/\D/g, '');
      items.push(
        <a 
          key="whatsapp" 
          href={`https://wa.me/${formattedPhone}`} 
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-sm text-teal-600 hover:underline"
        >
          <Phone className="w-4 h-4" />
          WhatsApp
        </a>
      );
    }
    if (links.website) {
      items.push(
        <a 
          key="website" 
          href={links.website.startsWith('http') ? links.website : `https://${links.website}`} 
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-sm text-teal-600 hover:underline"
        >
          <Globe className="w-4 h-4" />
          Site
          <ExternalLink className="w-3 h-3" />
        </a>
      );
    }
    
    return items;
  };

  return (
    <div 
      className={`bg-white rounded-xl border p-4 ${isActive ? 'border-teal-200' : 'border-gray-200'}`}
      data-testid={`job-card-${job.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            {getStatusBadge()}
            {job.business_name && (
              <span className="text-xs text-gray-500">{job.business_name}</span>
            )}
          </div>
          <h3 className="font-bold text-lg text-gray-900 truncate">{job.titulo}</h3>
          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{job.descricao}</p>
          
          <div className="flex items-center gap-2 mt-3 text-xs text-gray-500">
            <Calendar className="w-4 h-4" />
            <span>{formatDate(job.data_inicio)} - {formatDate(job.data_fim)}</span>
          </div>
          
          <div className="flex flex-wrap items-center gap-3 mt-3">
            {getApplicationLinks()}
          </div>

          {/* Analytics Metrics */}
          {showAnalytics && (job.views > 0 || job.clicks_total > 0) && (
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100">
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <Eye className="w-4 h-4" />
                <span><strong>{job.views || 0}</strong> visualizações</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <MousePointerClick className="w-4 h-4" />
                <span><strong>{job.clicks_total || 0}</strong> cliques</span>
              </div>
              {job.clicks_whatsapp > 0 && (
                <span className="text-xs text-green-600">
                  <Phone className="w-3 h-3 inline mr-1" />{job.clicks_whatsapp}
                </span>
              )}
              {job.clicks_email > 0 && (
                <span className="text-xs text-blue-600">
                  <Mail className="w-3 h-3 inline mr-1" />{job.clicks_email}
                </span>
              )}
              {job.clicks_website > 0 && (
                <span className="text-xs text-gray-600">
                  <Globe className="w-3 h-3 inline mr-1" />{job.clicks_website}
                </span>
              )}
            </div>
          )}

          {isClosed && job.was_filled !== null && (
            <div className="mt-3 p-2 bg-gray-50 rounded-lg text-xs">
              <p className="text-gray-600">
                <strong>Feedback:</strong> {job.was_filled ? 'Vaga preenchida' : 'Vaga não preenchida'}
                {job.helped_by_platform && ' | Plataforma ajudou'}
              </p>
            </div>
          )}
        </div>
        
        {isActive && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => onEdit(job)}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Editar vaga"
              data-testid={`edit-job-${job.id}`}
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={() => onClose(job)}
              disabled={isLoading}
              className="p-2 text-gray-500 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
              title="Encerrar vaga"
              data-testid={`close-job-${job.id}`}
            >
              <AlertCircle className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDelete(job)}
              disabled={isLoading}
              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Excluir vaga"
              data-testid={`delete-job-${job.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Create/Edit Job Modal
const JobFormModal = ({ isOpen, onClose, job, businesses, onSave, initialBusinessId }) => {
  const [formData, setFormData] = useState({
    business_id: '',
    titulo: '',
    descricao: '',
    email: '',
    whatsapp: '',
    website: '',
    data_inicio: '',
    data_fim: ''
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (job) {
      setFormData({
        business_id: job.business_id || '',
        titulo: job.titulo || '',
        descricao: job.descricao || '',
        email: job.application_links?.email || '',
        whatsapp: job.application_links?.whatsapp || '',
        website: job.application_links?.website || '',
        data_inicio: job.data_inicio || '',
        data_fim: job.data_fim || ''
      });
    } else {
      const today = new Date().toISOString().split('T')[0];
      const nextMonth = new Date();
      nextMonth.setMonth(nextMonth.getMonth() + 1);

      // Preferência: 1) negocio_id vindo do CTA da single page, 2) auto-select
      // quando o dono tem apenas 1 negócio, 3) vazio para escolher.
      const preselect = (initialBusinessId && businesses.some(b => b.id === initialBusinessId))
        ? initialBusinessId
        : (businesses.length >= 1 ? businesses[0].id : '');

      setFormData({
        business_id: preselect,
        titulo: '',
        descricao: '',
        email: '',
        whatsapp: '',
        website: '',
        data_inicio: today,
        data_fim: nextMonth.toISOString().split('T')[0]
      });
    }
    setErrors({});
  }, [job, businesses, isOpen]);

  const validate = () => {
    const newErrors = {};
    
    if (!formData.business_id) newErrors.business_id = 'Selecione um negócio';
    if (!formData.titulo.trim()) newErrors.titulo = 'Título é obrigatório';
    if (formData.titulo.length < 3) newErrors.titulo = 'Título deve ter pelo menos 3 caracteres';
    if (!formData.descricao.trim()) newErrors.descricao = 'Descrição é obrigatória';
    if (formData.descricao.length < 10) newErrors.descricao = 'Descrição deve ter pelo menos 10 caracteres';
    if (!formData.email && !formData.whatsapp && !formData.website) {
      newErrors.contact = 'Informe pelo menos uma forma de candidatura';
    }
    if (!formData.data_inicio) newErrors.data_inicio = 'Data de início é obrigatória';
    if (!formData.data_fim) newErrors.data_fim = 'Data de fim é obrigatória';
    if (formData.data_fim < formData.data_inicio) {
      newErrors.data_fim = 'Data de fim deve ser maior que data de início';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    setSaving(true);
    try {
      const payload = {
        titulo: formData.titulo,
        descricao: formData.descricao,
        application_links: {
          email: formData.email || null,
          whatsapp: formData.whatsapp || null,
          website: formData.website || null
        },
        data_inicio: formData.data_inicio,
        data_fim: formData.data_fim
      };

      if (job) {
        // Update existing job
        await api.put(`/jobs/${job.id}`, payload);
        toast.success('Vaga atualizada com sucesso!');
      } else {
        // Create new job
        await api.post(`/businesses/${formData.business_id}/jobs`, payload);
        toast.success('Vaga criada com sucesso!');
      }
      
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving job:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar vaga');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">
            {job ? 'Editar Vaga' : 'Nova Vaga'}
          </h2>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-gray-100 rounded-full"
            data-testid="close-job-modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Business Selection - Only show if multiple businesses or creating new */}
          {!job && businesses.length > 1 && (
            <div>
              <label className="block text-sm font-medium mb-2">
                Negócio <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.business_id}
                onChange={(e) => setFormData({ ...formData, business_id: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                data-testid="job-business-select"
              >
                <option value="">Selecione um negócio</option>
                {businesses.map(b => (
                  <option key={b.id} value={b.id}>{b.nome}</option>
                ))}
              </select>
              {errors.business_id && (
                <p className="text-red-500 text-xs mt-1">{errors.business_id}</p>
              )}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Título da Vaga <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.titulo}
              onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
              placeholder="Ex: Atendente de Loja"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              maxLength={100}
              data-testid="job-title-input"
            />
            {errors.titulo && (
              <p className="text-red-500 text-xs mt-1">{errors.titulo}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Descrição da Vaga <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.descricao}
              onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              placeholder="Descreva as responsabilidades, requisitos e benefícios..."
              rows={4}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              maxLength={2000}
              data-testid="job-description-input"
            />
            {errors.descricao && (
              <p className="text-red-500 text-xs mt-1">{errors.descricao}</p>
            )}
          </div>

          {/* Application Links */}
          <div className="space-y-3">
            <label className="block text-sm font-medium">
              Formas de Candidatura <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-gray-500">Informe pelo menos uma forma de contato</p>
            
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@exemplo.com"
                className="flex-1 px-4 py-2 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                data-testid="job-email-input"
              />
            </div>
            
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={formData.whatsapp}
                onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                placeholder="5511999999999"
                className="flex-1 px-4 py-2 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                data-testid="job-whatsapp-input"
              />
            </div>
            
            <div className="flex items-center gap-3">
              <Globe className="w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://site.com/vagas"
                className="flex-1 px-4 py-2 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                data-testid="job-website-input"
              />
            </div>
            
            {errors.contact && (
              <p className="text-red-500 text-xs">{errors.contact}</p>
            )}
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Data Início <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.data_inicio}
                onChange={(e) => setFormData({ ...formData, data_inicio: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                data-testid="job-start-date"
              />
              {errors.data_inicio && (
                <p className="text-red-500 text-xs mt-1">{errors.data_inicio}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Data Fim <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.data_fim}
                onChange={(e) => setFormData({ ...formData, data_fim: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                data-testid="job-end-date"
              />
              {errors.data_fim && (
                <p className="text-red-500 text-xs mt-1">{errors.data_fim}</p>
              )}
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 rounded-xl border border-gray-200 font-medium hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-3 rounded-xl bg-teal-600 text-white font-medium hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="save-job-button"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  {job ? 'Atualizar' : 'Criar Vaga'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Close Job Modal with Feedback
const CloseJobModal = ({ isOpen, onClose, job, onConfirm }) => {
  const [wasFilled, setWasFilled] = useState(null);
  const [helpedByPlatform, setHelpedByPlatform] = useState(null);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setWasFilled(null);
      setHelpedByPlatform(null);
      setComment('');
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (wasFilled === null) {
      toast.error('Por favor, indique se a vaga foi preenchida');
      return;
    }
    if (helpedByPlatform === null) {
      toast.error('Por favor, indique se a plataforma ajudou');
      return;
    }

    setSaving(true);
    try {
      await api.post(`/jobs/${job.id}/close`, {
        was_filled: wasFilled,
        helped_by_platform: helpedByPlatform,
        feedback_comment: comment || null
      });
      toast.success('Vaga encerrada com sucesso!');
      onConfirm();
      onClose();
    } catch (error) {
      console.error('Error closing job:', error);
      toast.error(error.response?.data?.detail || 'Erro ao encerrar vaga');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !job) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold">Encerrar Vaga</h2>
          <p className="text-sm text-gray-500 mt-1">
            Antes de encerrar, queremos saber como foi sua experiência
          </p>
        </div>
        
        <div className="p-4 space-y-4">
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="font-medium text-sm">{job.titulo}</p>
          </div>

          {/* Was Filled Question */}
          <div>
            <p className="font-medium mb-3">A vaga foi preenchida?</p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setWasFilled(true)}
                className={`flex-1 py-3 rounded-xl border-2 font-medium transition-all ${
                  wasFilled === true
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 hover:border-green-300'
                }`}
                data-testid="was-filled-yes"
              >
                Sim
              </button>
              <button
                type="button"
                onClick={() => setWasFilled(false)}
                className={`flex-1 py-3 rounded-xl border-2 font-medium transition-all ${
                  wasFilled === false
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-gray-200 hover:border-red-300'
                }`}
                data-testid="was-filled-no"
              >
                Não
              </button>
            </div>
          </div>

          {/* Platform Help Question */}
          <div>
            <p className="font-medium mb-3">A plataforma ajudou na contratação?</p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setHelpedByPlatform(true)}
                className={`flex-1 py-3 rounded-xl border-2 font-medium transition-all ${
                  helpedByPlatform === true
                    ? 'border-teal-500 bg-teal-50 text-teal-700'
                    : 'border-gray-200 hover:border-teal-300'
                }`}
                data-testid="helped-yes"
              >
                Sim
              </button>
              <button
                type="button"
                onClick={() => setHelpedByPlatform(false)}
                className={`flex-1 py-3 rounded-xl border-2 font-medium transition-all ${
                  helpedByPlatform === false
                    ? 'border-gray-500 bg-gray-50 text-gray-700'
                    : 'border-gray-200 hover:border-gray-400'
                }`}
                data-testid="helped-no"
              >
                Não
              </button>
            </div>
          </div>

          {/* Optional Comment */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Comentário (opcional)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Conte-nos mais sobre sua experiência..."
              rows={3}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              maxLength={500}
              data-testid="close-job-comment"
            />
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 rounded-xl border border-gray-200 font-medium hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 py-3 rounded-xl bg-orange-500 text-white font-medium hover:bg-orange-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="confirm-close-job"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Encerrando...
              </>
            ) : (
              'Encerrar Vaga'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Component
export default function MinhasVagas() {
  const { token, user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, active, closed, expired
  const [analytics, setAnalytics] = useState({ views: 0, clicks: 0 });
  
  // Modals
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [initialBusinessId, setInitialBusinessId] = useState(null);

  const loadData = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      // Load jobs with analytics
      const analyticsRes = await api.get('/profile/jobs/analytics');
      
      // Merge analytics data with jobs
      const jobsWithAnalytics = analyticsRes.data.jobs || [];
      setAnalytics(analyticsRes.data.totals || { views: 0, clicks: 0 });
      
      // Load full jobs data
      const jobsRes = await api.get('/profile/jobs');
      
      // Merge analytics into jobs
      const analyticsMap = {};
      jobsWithAnalytics.forEach(j => { analyticsMap[j.id] = j; });
      
      const mergedJobs = jobsRes.data.map(job => ({
        ...job,
        views: analyticsMap[job.id]?.views || 0,
        clicks_total: analyticsMap[job.id]?.clicks_total || 0,
        clicks_email: analyticsMap[job.id]?.clicks_email || 0,
        clicks_whatsapp: analyticsMap[job.id]?.clicks_whatsapp || 0,
        clicks_website: analyticsMap[job.id]?.clicks_website || 0
      }));
      
      setJobs(mergedJobs);
    } catch (error) {
      console.error('Error loading jobs:', error);
      toast.error('Erro ao carregar vagas');
    }

    // Load user's businesses (separate try-catch to ensure it always loads)
    try {
      const businessesRes = await api.get('/profile/businesses');
      setBusinesses(businessesRes.data.filter(b => b.status_moderacao === 'aprovado'));
    } catch (error) {
      console.error('Error loading businesses:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-open create modal when arriving via CTA link
  // (`/perfil?tab=vagas&new=vaga&negocio_id=<id>`).
  useEffect(() => {
    if (loading) return;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('new') === 'vaga') {
      const negocioId = sp.get('negocio_id');
      if (negocioId) setInitialBusinessId(negocioId);
      setSelectedJob(null);
      setIsFormModalOpen(true);
      // Consome os params para não reabrir em refresh
      sp.delete('new'); sp.delete('negocio_id');
      const qs = sp.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    }
  }, [loading]);

  const handleEdit = (job) => {
    setSelectedJob(job);
    setIsFormModalOpen(true);
  };

  const handleClose = (job) => {
    setSelectedJob(job);
    setIsCloseModalOpen(true);
  };

  const handleDelete = async (job) => {
    if (!window.confirm(`Tem certeza que deseja excluir a vaga "${job.titulo}"?`)) {
      return;
    }

    setDeleteLoading(true);
    try {
      await api.delete(`/jobs/${job.id}`);
      toast.success('Vaga excluída com sucesso!');
      loadData();
    } catch (error) {
      console.error('Error deleting job:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir vaga');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCreateNew = () => {
    setSelectedJob(null);
    setIsFormModalOpen(true);
  };

  const filteredJobs = jobs.filter(job => {
    if (filter === 'all') return true;
    return job.status === filter;
  });

  const activeCount = jobs.filter(j => j.status === 'active').length;
  const closedCount = jobs.filter(j => j.status === 'closed').length;
  const expiredCount = jobs.filter(j => j.status === 'expired').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (businesses.length === 0) {
    return (
      <div className="text-center py-12">
        <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-bold text-gray-700 mb-2">
          Você precisa de um negócio aprovado
        </h3>
        <p className="text-gray-500 mb-4">
          Para publicar vagas, primeiro cadastre e tenha seu negócio aprovado.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
            Minhas Vagas
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Gerencie suas vagas de emprego
          </p>
        </div>
        <button
          onClick={handleCreateNew}
          className="flex items-center gap-2 bg-teal-600 text-white hover:bg-teal-700 rounded-full px-6 py-2.5 font-semibold transition-all shadow-sm hover:shadow-md active:scale-95"
          data-testid="create-job-button"
        >
          <Plus className="w-4 h-4" />
          Nova Vaga
        </button>
      </div>

      {/* Analytics Summary */}
      {(analytics.views > 0 || analytics.clicks > 0) && (
        <div className="bg-gradient-to-r from-teal-50 to-blue-50 border border-teal-200 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="w-5 h-5 text-teal-600" />
            <h3 className="font-bold text-teal-800">Métricas das suas vagas</h3>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2">
                <Eye className="w-5 h-5 text-teal-600" />
                <p className="text-2xl font-bold text-teal-700">{analytics.views}</p>
              </div>
              <p className="text-sm text-teal-600">Visualizações</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2">
                <MousePointerClick className="w-5 h-5 text-blue-600" />
                <p className="text-2xl font-bold text-blue-700">{analytics.clicks}</p>
              </div>
              <p className="text-sm text-blue-600">Cliques em candidatura</p>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-green-700">{activeCount}</p>
          <p className="text-sm text-green-600">Ativas</p>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-gray-700">{closedCount}</p>
          <p className="text-sm text-gray-600">Encerradas</p>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-yellow-700">{expiredCount}</p>
          <p className="text-sm text-yellow-600">Expiradas</p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm text-gray-500">Filtrar:</span>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-teal-500/20"
          data-testid="jobs-filter"
        >
          <option value="all">Todas ({jobs.length})</option>
          <option value="active">Ativas ({activeCount})</option>
          <option value="closed">Encerradas ({closedCount})</option>
          <option value="expired">Expiradas ({expiredCount})</option>
        </select>
      </div>

      {/* Jobs List */}
      {filteredJobs.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">
            {filter === 'all' ? 'Nenhuma vaga cadastrada' : `Nenhuma vaga ${filter === 'active' ? 'ativa' : filter === 'closed' ? 'encerrada' : 'expirada'}`}
          </h3>
          {filter === 'all' && (
            <button
              onClick={handleCreateNew}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
            >
              <Plus className="w-4 h-4" />
              Criar primeira vaga
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredJobs.map(job => (
            <JobCard
              key={job.id}
              job={job}
              onEdit={handleEdit}
              onClose={handleClose}
              onDelete={handleDelete}
              isLoading={deleteLoading}
              showAnalytics={true}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      <JobFormModal
        isOpen={isFormModalOpen}
        onClose={() => { setIsFormModalOpen(false); setInitialBusinessId(null); }}
        job={selectedJob}
        businesses={businesses}
        onSave={loadData}
        initialBusinessId={initialBusinessId}
      />

      <CloseJobModal
        isOpen={isCloseModalOpen}
        onClose={() => setIsCloseModalOpen(false)}
        job={selectedJob}
        onConfirm={loadData}
      />
    </div>
  );
}
